import React, { useState, useEffect } from 'react';
import { Building2, Tag } from 'lucide-react';
import { apiClient } from '../../config/api';

const SetupOEMsPage = () => {
    const [oems, setOems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedOemId, setExpandedOemId] = useState(null);
    const [oemDetails, setOemDetails] = useState({});

    useEffect(() => {
        fetchOEMs();
    }, []);

    const fetchOEMs = async () => {
        try {
            setLoading(true);
            const data = await apiClient.get('/oems');
            setOems((data || []).filter(o => o.is_active !== false));
        } catch (error) {
            console.error('Error fetching OEMs:', error);
            setError('Failed to fetch OEMs');
        } finally {
            setLoading(false);
        }
    };

    const fetchOemDetails = async (oemId) => {
        try {
            const data = await apiClient.get(`/oems/${oemId}`);
            setOemDetails(prev => ({ ...prev, [oemId]: data }));
        } catch (error) {
            console.error('Error fetching OEM details:', error);
        }
    };

    const handleExpand = (oemId) => {
        if (expandedOemId === oemId) {
            setExpandedOemId(null);
        } else {
            setExpandedOemId(oemId);
            if (!oemDetails[oemId]) {
                fetchOemDetails(oemId);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading OEMs...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900">OEM Management</h1>
                <p className="text-gray-600 mt-1">View Original Equipment Manufacturers (OEMs) and their associated brands</p>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                OEM Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Groups
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Brands
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {oems.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                    No OEMs found.
                                </td>
                            </tr>
                        ) : (
                            oems.map((oem) => (
                                <React.Fragment key={oem.id}>
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <Building2 size={18} className="text-gray-400 mr-2" />
                                                <span className="font-medium text-gray-900">{oem.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-500 max-w-md truncate">
                                                {oem.description || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900">{oem.group_count || 0}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900">{oem.brand_count || 0}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleExpand(oem.id)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                {expandedOemId === oem.id ? 'Collapse' : 'View Details'}
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedOemId === oem.id && oemDetails[oem.id] && (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 bg-gray-50">
                                                <div className="space-y-4">
                                                    <div>
                                                        <h3 className="font-medium text-gray-900 mb-2">Associated Groups ({oemDetails[oem.id].groups?.length || 0})</h3>
                                                        {oemDetails[oem.id].groups && oemDetails[oem.id].groups.length > 0 ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {oemDetails[oem.id].groups.map(group => (
                                                                    <span key={group.id} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                                                                        {group.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500">No groups associated with this OEM</p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-medium text-gray-900 mb-2">Associated Brands ({oemDetails[oem.id].brands?.length || 0})</h3>
                                                        {oemDetails[oem.id].brands && oemDetails[oem.id].brands.length > 0 ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {oemDetails[oem.id].brands.map(brand => (
                                                                    <span key={brand.id} className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm flex items-center gap-1">
                                                                        <Tag size={12} />
                                                                        {brand.name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500">No brands associated with this OEM</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SetupOEMsPage;

